import React, { useState, useEffect, useRef, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TelnyxRTC } from '@telnyx/webrtc';
import { io } from 'socket.io-client';
import { AuthContext } from '../contexts/AuthContext';
import api from '../services/api';
import Messaging from './Messaging';

const SOCKET_URL = process.env.REACT_APP_API_URL || '';

// ── Ringtone (Web Audio API) ───────────────────────────────────────────────
function createRingtone(ctx) {
  if (!ctx) return { stop: () => {} };
  let stopped = false;
  let timerId = null;
  const singleBeep = (startOffset) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination);
    osc.type = 'sine'; osc.frequency.value = 880;
    const t0 = ctx.currentTime + startOffset;
    gain.gain.setValueAtTime(0, t0);
    gain.gain.linearRampToValueAtTime(0.9, t0 + 0.015);
    gain.gain.setValueAtTime(0.9, t0 + 0.13);
    gain.gain.linearRampToValueAtTime(0, t0 + 0.15);
    osc.start(t0); osc.stop(t0 + 0.16);
  };
  const playPattern = () => {
    if (stopped) return;
    ctx.resume().then(() => {
      if (stopped) return;
      singleBeep(0.05); singleBeep(0.30); singleBeep(0.55);
      timerId = setTimeout(playPattern, 2500);
    }).catch(() => {});
  };
  playPattern();
  return { stop: () => { stopped = true; clearTimeout(timerId); } };
}

function requestNotificationPermission() {
  if ('Notification' in window && Notification.permission === 'default')
    Notification.requestPermission().catch(() => {});
}

function showCallNotification(callerId) {
  if (!('Notification' in window) || Notification.permission !== 'granted') return null;
  try {
    return new Notification('📞 Incoming Call', {
      body: callerId ? `From: ${callerId}` : 'Portable PBX',
      icon: '/favicon.ico', tag: 'pbx-incoming-call', requireInteraction: true,
    });
  } catch (_) { return null; }
}

function startTitleFlash(callerId) {
  const originalTitle = document.title;
  const label = callerId ? `📞 ${callerId}` : '📞 INCOMING CALL';
  let toggled = false;
  const id = setInterval(() => { document.title = toggled ? label : originalTitle; toggled = !toggled; }, 800);
  return () => { clearInterval(id); document.title = originalTitle; };
}

// ─────────────────────────────────────────────────────────────────────────────
// ICONS
// ─────────────────────────────────────────────────────────────────────────────

const EASE = [0.22, 1, 0.36, 1];

function Ico({ children, className = 'h-5 w-5' }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
      className={className} aria-hidden="true">{children}</svg>
  );
}
function MicIcon({ className })      { return <Ico className={className}><path d="M12 3a3 3 0 0 1 3 3v6a3 3 0 1 1-6 0V6a3 3 0 0 1 3-3Z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><path d="M12 19v3"/><path d="M8 22h8"/></Ico>; }
function MicOffIcon({ className })   { return <Ico className={className}><path d="M12 3a3 3 0 0 1 3 3v3"/><path d="M9 9v3a3 3 0 0 0 4.6 2.5"/><path d="M19 10v2a7 7 0 0 1-2.1 5"/><path d="M5 10v2a7 7 0 0 0 7 7"/><path d="M12 19v3"/><path d="M8 22h8"/><path d="m4 4 16 16"/></Ico>; }
function PauseIcon({ className })    { return <Ico className={className}><path d="M8 5v14"/><path d="M16 5v14"/></Ico>; }
function PlayIcon({ className })     { return <Ico className={className}><path d="m8 5 11 7-11 7Z"/></Ico>; }
function PhoneOffIcon({ className }) { return <Ico className={className}><path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.4 19.4 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7l.4 2.3"/><path d="m15 9 6 6"/><path d="m21 9-6 6"/></Ico>; }
function RetryIcon({ className })    { return <Ico className={className}><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/></Ico>; }
function UsersIcon({ className })    { return <Ico className={className}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></Ico>; }
function PhoneFwdIcon({ className }) { return <Ico className={className}><polyline points="19 1 23 5 19 9"/><line x1="15" y1="5" x2="23" y2="5"/><path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.4 19.4 0 0 1-6-6 19.8 19.8 0 0 1-3.1-8.67A2 2 0 0 1 3.9 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.9Z"/></Ico>; }
function KeypadIcon({ className })   { return <Ico className={className}><rect x="2" y="2" width="5" height="5" rx="1"/><rect x="9.5" y="2" width="5" height="5" rx="1"/><rect x="17" y="2" width="5" height="5" rx="1"/><rect x="2" y="9.5" width="5" height="5" rx="1"/><rect x="9.5" y="9.5" width="5" height="5" rx="1"/><rect x="17" y="9.5" width="5" height="5" rx="1"/><rect x="2" y="17" width="5" height="5" rx="1"/><rect x="9.5" y="17" width="5" height="5" rx="1"/></Ico>; }

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

export default function Softphone({ onIncomingCallChange, onPstnIncoming, theme = 'dark', pstnAnswerPending = false, onPstnAnswerHandled, onVoicemail, unreadVoicemails = 0, onVideoCall }) {
  const { user } = useContext(AuthContext);

  // ── State (UNCHANGED wiring) ──────────────────────────────────────────────
  const [dialedNumber, setDialedNumber]   = useState('');
  const [callStatus, setCallStatus]       = useState('idle');
  const [callDuration, setCallDuration]   = useState(0);
  const [isMuted, setIsMuted]             = useState(false);
  const [isHeld, setIsHeld]               = useState(false);
  const [sipStatus, setSipStatus]         = useState('disconnected');
  const [error, setError]                 = useState('');
  const [callerNumbers, setCallerNumbers] = useState([]);
  const [isInbound, setIsInbound]         = useState(false);
  const [callerId, setCallerId]           = useState('');

  // ── UI state ──────────────────────────────────────────────────────────────
  const [showTransfer, setShowTransfer]     = useState(false);
  const [transferTarget, setTransferTarget] = useState('');
  const [showAddCall, setShowAddCall]       = useState(false);
  const [addCallTarget, setAddCallTarget]   = useState('');
  const [showDtmf, setShowDtmf]             = useState(false);
  const [dtmfInput, setDtmfInput]           = useState('');
  const [contacts, setContacts]             = useState([]);
  const [showQuickAdd, setShowQuickAdd]       = useState(false);
  const [quickAddName, setQuickAddName]       = useState('');
  const [quickAddSaving, setQuickAddSaving]   = useState(false);
  const [recentCalls, setRecentCalls]       = useState([]);
  const [callLogLoading, setCallLogLoading] = useState(false);

  // ── Slack state ───────────────────────────────────────────────────────────
  const [slackStatus, setSlackStatus]         = useState(null);
  const [slackMessages, setSlackMessages]     = useState([]);
  const [slackMsgLoading, setSlackMsgLoading] = useState(false);
  const [slackReply, setSlackReply]           = useState('');
  const [slackSending, setSlackSending]       = useState(false);
  const slackPollRef                          = useRef(null);
  const slackMsgEndRef                        = useRef(null);

  // ── Right-column resize / collapse state ──────────────────────────────────
  const [splitPct, setSplitPct] = useState(() => {
    try { const s = localStorage.getItem('pbx-split-pct'); return s ? parseFloat(s) : 38; } catch { return 38; }
  });
  const [msgCollapsed, setMsgCollapsed]     = useState(false);
  const [slackCollapsed, setSlackCollapsed] = useState(false);
  const rightColRef    = useRef(null);
  const isDragging     = useRef(false);
  const dragStartY     = useRef(0);
  const dragStartPct   = useRef(38);

  // ── Refs (UNCHANGED) ──────────────────────────────────────────────────────
  const clientRef            = useRef(null);
  const activeCallRef        = useRef(null);
  const remoteAudioRef       = useRef(null);
  const callTimerRef         = useRef(null);
  const ringtoneRef          = useRef(null);
  const notificationRef      = useRef(null);
  const stopTitleFlash       = useRef(null);
  const audioCtxRef          = useRef(null);
  const pstnBannerTimer      = useRef(null);
  const lastPstnCallSid      = useRef(null);
  const notifSocketRef       = useRef(null);
  const pstnAnswerPendingRef = useRef(false);

  // ── Drag-to-resize handler ─────────────────────────────────────────────────
  const handleDividerMouseDown = (e) => {
    e.preventDefault();
    isDragging.current   = true;
    dragStartY.current   = e.clientY;
    dragStartPct.current = splitPct;

    const onMove = (ev) => {
      if (!isDragging.current || !rightColRef.current) return;
      const totalH = rightColRef.current.getBoundingClientRect().height;
      if (totalH === 0) return;
      const deltaY   = ev.clientY - dragStartY.current;
      const deltaPct = (deltaY / totalH) * 100;
      const newPct   = Math.min(Math.max(dragStartPct.current + deltaPct, 18), 76);
      setSplitPct(newPct);
      try { localStorage.setItem('pbx-split-pct', String(newPct)); } catch {}
    };

    const onUp = () => {
      isDragging.current = false;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  // ── Slack: load status on mount, poll messages every 30s ─────────────────
  useEffect(() => {
    let cancelled = false;

    const loadStatus = async () => {
      try {
        const res = await api.get('/slack/status');
        if (!cancelled) {
          setSlackStatus(res.data);
          if (res.data.connected && res.data.channelId) {
            loadMessages();
          }
        }
      } catch {
        if (!cancelled) setSlackStatus({ connected: false });
      }
    };

    const loadMessages = async () => {
      setSlackMsgLoading(true);
      try {
        const res = await api.get('/slack/messages');
        if (!cancelled) {
          setSlackMessages(res.data);
          setTimeout(() => slackMsgEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
        }
      } catch {
        // Silently fail on polling errors
      } finally {
        if (!cancelled) setSlackMsgLoading(false);
      }
    };

    loadStatus();

    slackPollRef.current = setInterval(async () => {
      try {
        const statusRes = await api.get('/slack/status');
        if (!cancelled) setSlackStatus(statusRes.data);
        if (statusRes.data.connected && statusRes.data.channelId) {
          loadMessages();
        }
      } catch { /* ignore */ }
    }, 30000);

    return () => {
      cancelled = true;
      clearInterval(slackPollRef.current);
    };
  }, []);

  const handleSlackSend = async () => {
    const text = slackReply.trim();
    if (!text || slackSending) return;
    setSlackSending(true);
    try {
      await api.post('/slack/message', { text });
      setSlackReply('');
      const res = await api.get('/slack/messages');
      setSlackMessages(res.data);
      setTimeout(() => slackMsgEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    } catch { /* ignore */ } finally {
      setSlackSending(false);
    }
  };

  // ── Keyboard shortcuts ─────────────────────────────────────────────────────
  useEffect(() => {
    function onKey(e) {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.key.toLowerCase() === 'enter') {
        e.preventDefault();
        if (callStatus === 'ringing' && isInbound) handleAnswer();
        else if (callStatus === 'idle' && dialedNumber) handleDial();
      }
      if (mod && e.key.toLowerCase() === 'd') {
        e.preventDefault();
        document.getElementById('pbx-dial-input')?.focus();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [callStatus, isInbound, dialedNumber]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Sync pstnAnswerPending prop → ref ────────────────────────────────────
  useEffect(() => {
    pstnAnswerPendingRef.current = pstnAnswerPending;
  }, [pstnAnswerPending]);

  // ── PSTN notification socket ──────────────────────────────────────────────
  useEffect(() => {
    if (!user?.id) return;
    const sock = io(`${SOCKET_URL}/video`, {
      path: '/api/socket.io',
      transports: ['websocket', 'polling'],
    });
    sock.on('connect', () => {
      sock.emit('register', { userId: user.id, email: user.email, tenantId: user.tenantId, type: 'softphone' });
    });
    sock.on('pstn-incoming', ({ from, callSid, callControlId }) => {
      if (callSid && callSid === lastPstnCallSid.current) return;
      lastPstnCallSid.current = callSid || null;
      clearTimeout(pstnBannerTimer.current);
      const banner = { from: from || 'Unknown', callSid, callControlId };
      if (onPstnIncoming) onPstnIncoming(banner);
      pstnBannerTimer.current = setTimeout(() => {
        lastPstnCallSid.current = null;
        if (onPstnIncoming) onPstnIncoming(null);
      }, 30_000);
    });
    notifSocketRef.current = sock;
    return () => {
      clearTimeout(pstnBannerTimer.current);
      sock.disconnect();
    };
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Init ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    requestNotificationPermission();
    try { audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)(); } catch (_) {}
    const unlockAudio = () => {
      audioCtxRef.current?.resume().catch(() => {});
      document.removeEventListener('click', unlockAudio);
      document.removeEventListener('keydown', unlockAudio);
    };
    document.addEventListener('click', unlockAudio);
    document.addEventListener('keydown', unlockAudio);
    initSoftphone();
    return () => {
      document.removeEventListener('click', unlockAudio);
      document.removeEventListener('keydown', unlockAudio);
      try { clientRef.current?.disconnect(); } catch (_) {}
      stopAllAlerts();
      audioCtxRef.current?.close().catch(() => {});
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Call timer ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (callStatus === 'active') {
      callTimerRef.current = setInterval(() => setCallDuration(s => s + 1), 1000);
    } else {
      clearInterval(callTimerRef.current);
      setCallDuration(0);
    }
    return () => clearInterval(callTimerRef.current);
  }, [callStatus]);

  const stopAllAlerts = () => {
    ringtoneRef.current?.stop(); ringtoneRef.current = null;
    notificationRef.current?.close(); notificationRef.current = null;
    stopTitleFlash.current?.(); stopTitleFlash.current = null;
  };

  // ── Alerts ────────────────────────────────────────────────────────────────
  useEffect(() => {
    const isRinging = callStatus === 'ringing' && isInbound;
    onIncomingCallChange?.(isRinging);
    if (isRinging) {
      stopAllAlerts();
      ringtoneRef.current     = createRingtone(audioCtxRef.current);
      notificationRef.current = showCallNotification(callerId);
      stopTitleFlash.current  = startTitleFlash(callerId);
    } else {
      stopAllAlerts();
    }
  }, [callStatus, isInbound]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Softphone init ────────────────────────────────────────────────────────
  const initSoftphone = async () => {
    setSipStatus('connecting'); setError('');
    try {
      await api.post('/softphone/provision');
      const { data: config } = await api.get('/softphone/config');
      setCallerNumbers(config.callerNumbers || []);

      const client = new TelnyxRTC({
        login: config.sip.username, password: config.sip.password,
        iceServers: [
          { urls: 'stun:stun.l.google.com:19302' },
          { urls: config.turn.urls, username: config.turn.username, credential: config.turn.credential },
        ],
      });

      client.on('telnyx.ready', () => { setSipStatus('registered'); setError(''); });
      client.on('telnyx.error', (err) => { console.error('[Telnyx] error:', err); setError('Connection error'); setSipStatus('error'); });
      client.on('telnyx.socket.close', () => setSipStatus('disconnected'));
      client.on('telnyx.socket.error', (err) => { console.error('[Telnyx] socket error:', err); setSipStatus('error'); });

      client.on('telnyx.notification', (notification) => {
        if (notification.type !== 'callUpdate') return;
        const call = notification.call;
        console.log('[Telnyx] call state:', call.state, 'direction:', call.direction, 'from:', call.from);
        activeCallRef.current = call;
        if (call.remoteStream && remoteAudioRef.current) {
          remoteAudioRef.current.srcObject = call.remoteStream;
          remoteAudioRef.current.play().catch(console.error);
        }
        const inbound = call.direction === 'inbound';
        switch (call.state) {
          case 'new': case 'trying': case 'requesting':
            setIsInbound(inbound); if (inbound) setCallerId(call.from || ''); setCallStatus('dialing');
            if (inbound && pstnAnswerPendingRef.current) {
              console.log('[Telnyx] PSTN transfer — auto-answering at new state');
              pstnAnswerPendingRef.current = false;
              onPstnAnswerHandled?.();
              setTimeout(() => call.answer(), 100);
            }
            break;
          case 'ringing': case 'early':
            setIsInbound(inbound); if (inbound) setCallerId(call.from || ''); setCallStatus('ringing');
            if (inbound && pstnAnswerPendingRef.current) {
              console.log('[Telnyx] PSTN transfer — auto-answering WebRTC leg');
              pstnAnswerPendingRef.current = false;
              onPstnAnswerHandled?.();
              setTimeout(() => call.answer(), 100);
            }
            break;
          case 'answering': case 'active':
            setCallStatus('active'); break;
          case 'held':
            setIsHeld(true); break;
          case 'hangup': case 'destroy': case 'purge':
            activeCallRef.current = null;
            setCallStatus('idle'); setDialedNumber(''); setCallerId('');
            setIsMuted(false); setIsHeld(false); setIsInbound(false);
            setShowQuickAdd(false); setQuickAddName(''); break;
          default: break;
        }
      });

      clientRef.current = client;
      client.connect();
    } catch (err) {
      console.error('[Telnyx] init error:', err);
      if (err?.response?.status === 400) {
        setError('No carrier connected — complete the Setup Wizard to enable calling.');
      } else {
        setError('Could not connect: ' + (err?.message || String(err)));
      }
      setSipStatus('error');
    }
  };

  // ── Call controls ─────────────────────────────────────────────────────────
  const handleDial = () => {
    if (!dialedNumber.trim() || !clientRef.current || sipStatus !== 'registered') return;
    setError('');
    const callerNumber = callerNumbers[0] || '+17027606299';
    const destination  = dialedNumber.replace(/[^\d+]/g, '') || dialedNumber;
    clientRef.current.newCall({ destinationNumber: destination, callerNumber, audio: true, video: false });
  };
  const dialTo = (number) => {
    if (!number || !clientRef.current || sipStatus !== 'registered') return;
    setError('');
    const callerNumber = callerNumbers[0] || '+17027606299';
    const destination  = number.replace(/[^\d+]/g, '') || number;
    setDialedNumber(number);
    clientRef.current.newCall({ destinationNumber: destination, callerNumber, audio: true, video: false });
  };
  const handleAnswer  = () => { stopAllAlerts(); activeCallRef.current?.answer(); };
  const handleDecline = () => { stopAllAlerts(); activeCallRef.current?.hangup(); setCallStatus('idle'); setIsInbound(false); setCallerId(''); };
  const handleHangup  = () => {
    stopAllAlerts(); activeCallRef.current?.hangup(); activeCallRef.current = null;
    setCallStatus('idle'); setDialedNumber(''); setCallerId('');
    setIsMuted(false); setIsHeld(false); setIsInbound(false);
  };
  const handleMute = () => {
    if (!activeCallRef.current) return;
    isMuted ? activeCallRef.current.unmuteAudio() : activeCallRef.current.muteAudio();
    setIsMuted(m => !m);
  };
  const handleHold = () => {
    if (!activeCallRef.current) return;
    isHeld ? activeCallRef.current.unhold() : activeCallRef.current.hold();
  };
  const handleTransfer = () => {
    if (!transferTarget.trim() || !activeCallRef.current) return;
    try { activeCallRef.current.transfer(transferTarget.replace(/[^\d+]/g, '')); } catch (e) { console.warn('[Transfer]', e); }
    setShowTransfer(false); setTransferTarget('');
  };
  const handleAddCall = () => {
    if (!addCallTarget.trim() || !clientRef.current) return;
    const dest = addCallTarget.replace(/[^\d+]/g, '');
    clientRef.current.newCall({ destinationNumber: dest, callerNumber: callerNumbers[0] || '+17027606299', audio: true, video: false });
    setShowAddCall(false); setAddCallTarget('');
  };
  const handleDtmf = (key) => {
    if (!activeCallRef.current) return;
    try { activeCallRef.current.dtmf(key); } catch (e) { console.warn('[DTMF]', e); }
    setDtmfInput(prev => prev + key);
  };
  const fetchContacts = async () => {
    try { const res = await api.get('/contacts'); setContacts(res.data || []); } catch (_) {}
  };
  const handleQuickSave = async (number) => {
    if (!quickAddName.trim() || !number) return;
    setQuickAddSaving(true);
    try {
      await api.post('/contacts', { name: quickAddName.trim(), phoneNumber: number });
      await fetchContacts();
      setShowQuickAdd(false);
      setQuickAddName('');
    } catch (e) { console.warn('[QuickAdd]', e); }
    finally { setQuickAddSaving(false); }
  };

  const lookupContact = (number) => {
    if (!number || !contacts.length) return null;
    const norm = (n) => String(n).replace(/\D/g, '').slice(-10);
    const target = norm(number);
    if (!target) return null;
    const match = contacts.find(c => norm(c.phoneNumber) === target);
    return match ? match.name : null;
  };

  const formatDuration = s => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

  // ── Dialpad ───────────────────────────────────────────────────────────────
  const DIALPAD_KEYS = [
    { key: '1', sub: '' },    { key: '2', sub: 'ABC' }, { key: '3', sub: 'DEF' },
    { key: '4', sub: 'GHI' }, { key: '5', sub: 'JKL' }, { key: '6', sub: 'MNO' },
    { key: '7', sub: 'PQRS'},{ key: '8', sub: 'TUV' }, { key: '9', sub: 'WXYZ'},
    { key: '*', sub: '' },    { key: '0', sub: '+' },   { key: '#', sub: '' },
  ];
  const handleDialpadPress = (key) => setDialedNumber(prev => prev + key);
  const handleBackspace = () => setDialedNumber(prev => prev.slice(0, -1));

  // ── Mini call log ─────────────────────────────────────────────────────────
  const fetchRecentCalls = async () => {
    try {
      setCallLogLoading(true);
      const res = await api.get('/calls', { params: { limit: 8 } });
      setRecentCalls(res.data.data || []);
    } catch (_) {}
    finally { setCallLogLoading(false); }
  };

  useEffect(() => {
    fetchRecentCalls();
    const t = setInterval(fetchRecentCalls, 30000);
    return () => clearInterval(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchContacts(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (callStatus === 'idle') fetchRecentCalls();
  }, [callStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatCallTime = (iso) => {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1)  return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHrs = Math.floor(diffMins / 60);
    if (diffHrs < 24)  return `${diffHrs}h ago`;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  const miniStatusStyle = (status) => {
    const isDark = theme === 'dark';
    if (isDark) {
      switch (status) {
        case 'completed': return 'bg-green-500/20 text-green-400';
        case 'missed':    return 'bg-red-500/20 text-red-400';
        case 'active':    return 'bg-blue-500/20 text-blue-400';
        default:          return 'bg-white/10 text-white/40';
      }
    }
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-700';
      case 'missed':    return 'bg-red-100 text-red-700';
      case 'active':    return 'bg-blue-100 text-blue-700';
      default:          return 'bg-slate-100 text-slate-500';
    }
  };

  // ── Derived ───────────────────────────────────────────────────────────────
  const callIsActive   = callStatus === 'active';
  const showIncoming   = callStatus === 'ringing' && isInbound;
  const showOutRinging = callStatus === 'ringing' && !isInbound;
  const showDialing    = callStatus === 'dialing';

  const resolvedCallerName = callerId ? lookupContact(callerId) : null;
  const heroName = callIsActive || showIncoming
    ? (resolvedCallerName || callerId || 'Unknown')
    : (dialedNumber || '');

  const statusDot   = callIsActive   ? '● Connected'
    : showIncoming   ? '● Incoming'
    : showDialing    ? '● Dialing…'
    : showOutRinging ? '● Ringing…'
    : sipStatus === 'registered'  ? '● Ready'
    : sipStatus === 'connecting'  ? '● Connecting…'
    : sipStatus === 'error'       ? '● Error'
    : '● Offline';

  const dotColor = callIsActive     ? 'text-emerald-400'
    : showIncoming   ? 'text-blue-400'
    : (showDialing || showOutRinging) ? 'text-amber-400'
    : sipStatus === 'registered'  ? 'text-emerald-400'
    : sipStatus === 'error'       ? 'text-red-400'
    : 'text-white/25';

  // ── Theme ─────────────────────────────────────────────────────────────────
  const panel = theme === 'dark' ? 'bg-black/40 border-white/10'   : 'bg-white border-slate-200';
  const soft  = theme === 'dark' ? 'bg-white/[0.07]'               : 'bg-slate-100';
  const muted = theme === 'dark' ? 'text-white/40'                 : 'text-slate-400';
  const field = theme === 'dark'
    ? 'bg-white/[0.07] text-white placeholder:text-white/25 outline-none'
    : 'bg-slate-100 text-slate-900 placeholder:text-slate-400 outline-none';

  // ── Right column layout helpers ───────────────────────────────────────────
  const COLLAPSED_H  = 52;  // px — just enough to show the card header
  const showDivider  = !msgCollapsed && !slackCollapsed;

  // Top card (Messages) height
  const msgStyle = msgCollapsed
    ? { height: `${COLLAPSED_H}px`, flexShrink: 0 }
    : slackCollapsed
      ? { flex: '1 1 auto', minHeight: 0 }
      : { height: `${splitPct}%`, flexShrink: 0 };

  // Bottom card (Slack) height
  const slackStyle = slackCollapsed
    ? { height: `${COLLAPSED_H}px`, flexShrink: 0 }
    : { flex: '1 1 auto', minHeight: 0 };

  // Shared chevron SVG helper
  const Chevron = ({ up }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
      <path d={up ? 'm6 15 6-6 6 6' : 'm6 9 6 6 6-6'} />
    </svg>
  );

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="grid grid-cols-[1.4fr_1fr] gap-6 h-full">

      {/* CRITICAL: hidden remote audio */}
      <audio ref={remoteAudioRef} autoPlay playsInline style={{ display: 'none' }} />

      {/* ── LEFT: Hero call panel ─────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: EASE }}
        className={`rounded-[32px] border p-8 flex flex-col backdrop-blur-xl ${panel}`}
      >
        <div>
          {/* Top row: Voicemail + Ready / Video Call */}
          <div className="flex items-center justify-between">
            {callIsActive || showIncoming ? (
              <div className={`text-xs uppercase tracking-widest ${muted}`}>
                {callIsActive ? 'Active Session' : 'Incoming Call'}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  onClick={onVoicemail}
                  className={`relative inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest px-2.5 py-1 rounded-lg transition ${
                    theme === 'dark'
                      ? 'text-white/50 hover:text-white hover:bg-white/10'
                      : 'text-slate-400 hover:text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  Voicemail
                  {unreadVoicemails > 0 && (
                    <span className="inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold">
                      {unreadVoicemails}
                    </span>
                  )}
                </button>
                <span className={`text-xs font-medium ${dotColor}`}>{statusDot}</span>
              </div>
            )}
            {!callIsActive && !showIncoming && (
              <button
                onClick={onVideoCall}
                className={`inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest px-2.5 py-1 rounded-lg transition ${
                  theme === 'dark'
                    ? 'text-white/50 hover:text-white hover:bg-white/10'
                    : 'text-slate-400 hover:text-slate-700 hover:bg-slate-100'
                }`}
              >
                Video Call
              </button>
            )}
          </div>

          {/* Hero name / number input */}
          {callIsActive || showIncoming || showDialing || showOutRinging ? (
            <>
              <div className="text-5xl font-semibold mt-4 tracking-tight leading-tight">{heroName}</div>
              {(callIsActive || showIncoming) && resolvedCallerName && callerId && (
                <div className={`text-lg mt-1 font-mono ${muted}`}>{callerId}</div>
              )}
              {callerNumbers[0] && <div className={`text-xl mt-2 ${muted}`}>{callerNumbers[0]}</div>}
              {/* Quick-add to contacts */}
              {(() => {
                const activeNum = (callIsActive || showIncoming) ? callerId : dialedNumber;
                if (!activeNum || lookupContact(activeNum)) return null;
                return showQuickAdd ? (
                  <div className="flex items-center gap-2 mt-3">
                    <input
                      autoFocus
                      value={quickAddName}
                      onChange={e => setQuickAddName(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') handleQuickSave(activeNum); if (e.key === 'Escape') { setShowQuickAdd(false); setQuickAddName(''); } }}
                      placeholder="Contact name…"
                      className={`flex-1 rounded-xl px-3 py-2 text-sm outline-none ${field} ${soft}`}
                    />
                    <motion.button whileTap={{ scale: 0.95 }}
                      onClick={() => handleQuickSave(activeNum)}
                      disabled={quickAddSaving || !quickAddName.trim()}
                      className="px-3 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-white text-sm font-medium transition disabled:opacity-40">
                      {quickAddSaving ? '…' : 'Save'}
                    </motion.button>
                    <motion.button whileTap={{ scale: 0.95 }}
                      onClick={() => { setShowQuickAdd(false); setQuickAddName(''); }}
                      className={`px-3 py-2 rounded-xl text-sm transition ${soft} ${muted}`}>
                      ✕
                    </motion.button>
                  </div>
                ) : (
                  <motion.button whileTap={{ scale: 0.95 }}
                    onClick={() => setShowQuickAdd(true)}
                    className={`mt-3 inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-xl transition ${soft} ${muted} hover:text-current`}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5">
                      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="16" y1="11" x2="22" y2="11"/>
                    </svg>
                    Save to Contacts
                  </motion.button>
                );
              })()}
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 mt-4">
                <input
                  id="pbx-dial-input"
                  type="text"
                  value={dialedNumber}
                  onChange={e => setDialedNumber(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleDial()}
                  placeholder="Enter number…"
                  className={`flex-1 bg-transparent text-4xl font-semibold tracking-tight placeholder:text-white/20 outline-none min-w-0`}
                />
                {dialedNumber && (
                  <motion.button whileTap={{ scale: 0.9 }} onClick={handleBackspace}
                    className={`p-2 rounded-xl ${soft} ${muted} hover:text-current transition flex-shrink-0`}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
                      strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
                      <path d="M21 4H8l-7 8 7 8h13a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2Z"/>
                      <path d="m15 9-6 6"/><path d="m9 9 6 6"/>
                    </svg>
                  </motion.button>
                )}
              </div>

              <div className="flex justify-center mt-3 mb-1">
                <img src="/LOGO.png" alt="Only Number"
                  className="max-w-[140px] w-full object-contain opacity-50"
                  onError={e => { e.currentTarget.style.display = 'none'; }} />
              </div>

              <div className="mt-2 grid grid-cols-3 gap-2">
                {DIALPAD_KEYS.map(({ key, sub }) => (
                  <motion.button key={key} whileTap={{ scale: 0.92 }}
                    onClick={() => handleDialpadPress(key)}
                    className={`rounded-2xl py-3 flex flex-col items-center justify-center border border-white/5 transition select-none ${soft}`}>
                    <span className="text-xl font-semibold leading-none">{key}</span>
                    {sub && <span className={`text-[9px] tracking-widest mt-0.5 ${muted}`}>{sub}</span>}
                  </motion.button>
                ))}
              </div>
            </>
          )}

          {(callIsActive || showDialing || showOutRinging || showIncoming) && (
            <motion.div animate={{ opacity: [0.5, 1, 0.5] }} transition={{ duration: 2, repeat: Infinity }}
              className={`mt-4 text-sm font-medium ${dotColor}`}>
              {statusDot}{callIsActive && ` • ${formatDuration(callDuration)}`}
            </motion.div>
          )}

          <AnimatePresence>
            {error && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-4 rounded-2xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-400">
                {error}
                <button onClick={initSoftphone} className="ml-3 inline-flex items-center gap-1 underline">
                  <RetryIcon className="h-3.5 w-3.5" /> Retry
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {callIsActive && (
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 8 }} className="mt-8 grid grid-cols-2 gap-3">
                <motion.button whileTap={{ scale: 0.95 }} onClick={handleMute}
                  className={`rounded-2xl py-4 text-sm font-medium transition border ${
                    isMuted ? 'bg-amber-500/20 text-amber-300 border-amber-500/20' : `${soft} border-white/5`
                  }`}>
                  <div className="flex justify-center mb-1.5">{isMuted ? <MicOffIcon className="h-4 w-4"/> : <MicIcon className="h-4 w-4"/>}</div>
                  {isMuted ? 'Unmute' : 'Mute'}
                </motion.button>
                <motion.button whileTap={{ scale: 0.95 }} onClick={handleHold}
                  className={`rounded-2xl py-4 text-sm font-medium transition border ${
                    isHeld ? 'bg-blue-500/20 text-blue-300 border-blue-500/20' : `${soft} border-white/5`
                  }`}>
                  <div className="flex justify-center mb-1.5">{isHeld ? <PlayIcon className="h-4 w-4"/> : <PauseIcon className="h-4 w-4"/>}</div>
                  {isHeld ? 'Resume' : 'Hold'}
                </motion.button>
                <motion.button whileTap={{ scale: 0.95 }} onClick={() => setShowAddCall(true)}
                  className={`rounded-2xl py-4 text-sm font-medium border border-white/5 transition ${soft}`}>
                  <div className="flex justify-center mb-1.5"><UsersIcon className="h-4 w-4"/></div>
                  Add / Merge
                </motion.button>
                <motion.button whileTap={{ scale: 0.95 }} onClick={() => setShowTransfer(true)}
                  className={`rounded-2xl py-4 text-sm font-medium border border-white/5 transition ${soft}`}>
                  <div className="flex justify-center mb-1.5"><PhoneFwdIcon className="h-4 w-4"/></div>
                  Transfer
                </motion.button>
                <motion.button whileTap={{ scale: 0.95 }} onClick={() => { setDtmfInput(''); setShowDtmf(true); }}
                  className={`col-span-2 rounded-2xl py-4 text-sm font-medium border border-white/5 transition ${soft}`}>
                  <div className="flex justify-center mb-1.5"><KeypadIcon className="h-4 w-4"/></div>
                  Keypad
                </motion.button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {callStatus === 'idle' && (
          <motion.button whileTap={{ scale: 0.96 }} onClick={handleDial}
            disabled={!dialedNumber.trim() || sipStatus !== 'registered'}
            className="mt-2 w-full bg-emerald-500 hover:bg-emerald-400 py-5 rounded-2xl text-white font-medium text-lg transition disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0">
            {sipStatus !== 'registered' ? 'Waiting…' : 'Call'}
          </motion.button>
        )}

        {callStatus === 'idle' && (
          <div className="flex-shrink-0 flex flex-col mt-3">
            <div className="mb-2 flex items-center justify-between">
              <div className={`text-xs uppercase tracking-widest ${muted}`}>Recent Calls</div>
              <motion.button whileTap={{ scale: 0.9 }} onClick={fetchRecentCalls} title="Refresh"
                className={`text-sm transition ${muted} hover:opacity-70 ${callLogLoading ? 'animate-spin' : ''}`}>↻</motion.button>
            </div>
            <div className="overflow-y-auto space-y-1" style={{ maxHeight: '116px' }}>
              {recentCalls.length === 0 ? (
                <div className={`text-sm ${muted}`}>{callLogLoading ? 'Loading…' : 'No calls yet'}</div>
              ) : (
                recentCalls.map((call, i) => (
                  <motion.div key={call.id} initial={{ opacity: 0, x: -4 }} animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.03 }}
                    onDoubleClick={() => dialTo(call.direction === 'inbound' ? call.fromNumber : call.toNumber)}
                    title="Double-click to call"
                    className={`group flex items-center gap-2.5 px-3 py-2 rounded-2xl transition cursor-pointer ${soft} hover:ring-1 ${theme === 'dark' ? 'hover:ring-white/20' : 'hover:ring-slate-300'}`}>
                    <span className={`text-sm font-bold flex-shrink-0 ${
                      call.direction === 'inbound'
                        ? (theme === 'dark' ? 'text-sky-400' : 'text-blue-500')
                        : (theme === 'dark' ? 'text-violet-400' : 'text-purple-500')
                    }`}>{call.direction === 'inbound' ? '↓' : '↑'}</span>
                    <span className={`flex-1 text-xs truncate ${theme === 'dark' ? 'text-white/70' : 'text-slate-700'}`}>
                      {(() => { const num = call.direction === 'inbound' ? call.fromNumber : call.toNumber; const name = lookupContact(num); return name ? (<><span className="font-semibold not-italic">{name}</span><span className={`block font-mono opacity-50`}>{num}</span></>) : (<span className="font-mono">{num}</span>); })()}
                    </span>
                    <span className={`text-xs flex-shrink-0 opacity-0 group-hover:opacity-100 transition ${theme === 'dark' ? 'text-emerald-400' : 'text-emerald-600'}`}>📞</span>
                    <span className={`text-xs flex-shrink-0 ${muted}`}>{formatCallTime(call.startedAt)}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold flex-shrink-0 ${miniStatusStyle(call.status)}`}>
                      {call.status}
                    </span>
                  </motion.div>
                ))
              )}
            </div>
          </div>
        )}

        {(showIncoming || showDialing || showOutRinging || callIsActive) && (
          <div className="flex gap-4 mt-auto pt-2">
            {showIncoming && (
              <>
                <motion.button whileTap={{ scale: 0.96 }} onClick={handleAnswer}
                  className="flex-1 bg-emerald-500 hover:bg-emerald-400 py-5 rounded-2xl text-white font-medium text-lg transition">
                  Answer
                </motion.button>
                <motion.button whileTap={{ scale: 0.96 }} onClick={handleDecline}
                  className={`flex-1 py-5 rounded-2xl font-medium text-lg border border-white/10 transition ${soft}`}>
                  Decline
                </motion.button>
              </>
            )}
            {(showDialing || showOutRinging) && (
              <motion.button whileTap={{ scale: 0.96 }} onClick={handleHangup}
                className="flex-1 bg-red-500 hover:bg-red-400 py-5 rounded-2xl text-white font-medium text-lg transition">
                Cancel
              </motion.button>
            )}
            {callIsActive && (
              <motion.button whileTap={{ scale: 0.96 }} onClick={handleHangup}
                className="flex-1 bg-red-500 hover:bg-red-400 py-5 rounded-2xl text-white font-medium text-lg transition flex items-center justify-center gap-2">
                <PhoneOffIcon className="h-5 w-5"/> End
              </motion.button>
            )}
          </div>
        )}

      </motion.div>

      {/* ── RIGHT: Two resizable + collapsible cards ───────────────────────── */}
      <div ref={rightColRef} className="flex flex-col h-full min-h-0 select-none">

        {/* ── TOP: Messages (SMS) ───────────────────────────────────────────── */}
        <div
          className="relative overflow-hidden rounded-[32px] flex-shrink-0"
          style={{
            ...msgStyle,
            transition: 'height 0.22s cubic-bezier(0.22,1,0.36,1)',
          }}
        >
          {/* Messaging component fills the container */}
          <div className="absolute inset-0">
            <Messaging theme={theme} />
          </div>

          {/* Collapse/expand toggle — floated top-right */}
          <button
            onClick={() => setMsgCollapsed(c => !c)}
            title={msgCollapsed ? 'Expand Messages' : 'Collapse Messages'}
            className={`absolute top-2.5 right-2.5 z-10 w-6 h-6 rounded-lg flex items-center justify-center transition backdrop-blur-sm ${
              theme === 'dark'
                ? 'bg-black/30 hover:bg-white/10 text-white/35 hover:text-white/80'
                : 'bg-white/70 hover:bg-slate-100 text-slate-300 hover:text-slate-600'
            }`}
          >
            <Chevron up={!msgCollapsed} />
          </button>
        </div>

        {/* ── DRAG DIVIDER (shown only when both cards are open) ────────────── */}
        {showDivider ? (
          <div
            onMouseDown={handleDividerMouseDown}
            className="flex-shrink-0 flex items-center justify-center group cursor-row-resize"
            style={{ height: '20px' }}
            title="Drag to resize"
          >
            <div className={`h-1 rounded-full transition-all duration-150 group-hover:h-1.5 ${
              theme === 'dark'
                ? 'w-8 bg-white/[0.08] group-hover:w-14 group-hover:bg-emerald-400/50'
                : 'w-8 bg-slate-200 group-hover:w-14 group-hover:bg-emerald-400'
            }`} />
          </div>
        ) : (
          <div style={{ height: '8px', flexShrink: 0 }} />
        )}

        {/* ── BOTTOM: Slack Messages ────────────────────────────────────────── */}
        <div
          className={`rounded-[32px] border flex flex-col backdrop-blur-xl overflow-hidden ${panel}`}
          style={{
            ...slackStyle,
            transition: 'height 0.22s cubic-bezier(0.22,1,0.36,1)',
          }}
        >
          {/* Header */}
          <div className={`px-5 pt-4 pb-3 flex items-center justify-between flex-shrink-0 border-b ${
            theme === 'dark' ? 'border-white/5' : 'border-slate-100'
          }`}>
            <div className="flex items-center gap-2 min-w-0">
              <svg width="14" height="14" viewBox="0 0 54 54" fill="none" className="flex-shrink-0">
                <path d="M19.7 33.5a4.3 4.3 0 1 1-4.3-4.3h4.3v4.3z" fill="#E01E5A"/>
                <path d="M21.9 33.5a4.3 4.3 0 0 1 8.6 0v10.7a4.3 4.3 0 1 1-8.6 0V33.5z" fill="#E01E5A"/>
                <path d="M26.2 19.7a4.3 4.3 0 1 1 4.3-4.3v4.3h-4.3z" fill="#36C5F0"/>
                <path d="M26.2 21.9a4.3 4.3 0 0 1 0 8.6H15.5a4.3 4.3 0 1 1 0-8.6h10.7z" fill="#36C5F0"/>
                <path d="M40 26.2a4.3 4.3 0 1 1 4.3 4.3H40v-4.3z" fill="#2EB67D"/>
                <path d="M37.8 26.2a4.3 4.3 0 0 1-8.6 0V15.5a4.3 4.3 0 1 1 8.6 0v10.7z" fill="#2EB67D"/>
                <path d="M33.5 40a4.3 4.3 0 1 1-4.3 4.3V40h4.3z" fill="#ECB22E"/>
                <path d="M33.5 37.8a4.3 4.3 0 0 1 0-8.6h10.7a4.3 4.3 0 1 1 0 8.6H33.5z" fill="#ECB22E"/>
              </svg>
              <span className={`text-xs uppercase tracking-widest truncate ${muted}`}>
                {slackStatus?.channelName ? `#${slackStatus.channelName}` : 'Slack'}
              </span>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {slackStatus?.connected && (
                <div className="flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-400 flex-shrink-0" />
                  <span className={`text-xs truncate max-w-[80px] ${muted}`}>{slackStatus.teamName}</span>
                </div>
              )}
              {/* Collapse/expand toggle */}
              <button
                onClick={() => setSlackCollapsed(c => !c)}
                title={slackCollapsed ? 'Expand Slack' : 'Collapse Slack'}
                className={`w-6 h-6 rounded-lg flex items-center justify-center transition ${
                  theme === 'dark'
                    ? 'text-white/30 hover:text-white/70 hover:bg-white/10'
                    : 'text-slate-300 hover:text-slate-600 hover:bg-slate-100'
                }`}
              >
                <Chevron up={slackCollapsed} />
              </button>
            </div>
          </div>

          {/* Body — rendered only when not collapsed */}
          {!slackCollapsed && (
            <>
              <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3 flex flex-col gap-2">
                {slackStatus === null && (
                  <div className="flex-1 flex items-center justify-center">
                    <span className={`text-xs ${muted}`}>Loading...</span>
                  </div>
                )}

                {slackStatus && !slackStatus.connected && (
                  <div className="flex-1 flex flex-col items-center justify-center gap-2 py-4">
                    <svg width="32" height="32" viewBox="0 0 54 54" fill="none" opacity="0.25">
                      <path d="M19.7 33.5a4.3 4.3 0 1 1-4.3-4.3h4.3v4.3z" fill="#E01E5A"/>
                      <path d="M21.9 33.5a4.3 4.3 0 0 1 8.6 0v10.7a4.3 4.3 0 1 1-8.6 0V33.5z" fill="#E01E5A"/>
                      <path d="M26.2 19.7a4.3 4.3 0 1 1 4.3-4.3v4.3h-4.3z" fill="#36C5F0"/>
                      <path d="M26.2 21.9a4.3 4.3 0 0 1 0 8.6H15.5a4.3 4.3 0 1 1 0-8.6h10.7z" fill="#36C5F0"/>
                      <path d="M40 26.2a4.3 4.3 0 1 1 4.3 4.3H40v-4.3z" fill="#2EB67D"/>
                      <path d="M37.8 26.2a4.3 4.3 0 0 1-8.6 0V15.5a4.3 4.3 0 1 1 8.6 0v10.7z" fill="#2EB67D"/>
                      <path d="M33.5 40a4.3 4.3 0 1 1-4.3 4.3V40h4.3z" fill="#ECB22E"/>
                      <path d="M33.5 37.8a4.3 4.3 0 0 1 0-8.6h10.7a4.3 4.3 0 1 1 0 8.6H33.5z" fill="#ECB22E"/>
                    </svg>
                    <p className={`text-xs text-center leading-relaxed ${muted}`}>
                      Connect Slack in Settings to see messages here.
                    </p>
                  </div>
                )}

                {slackStatus?.connected && !slackStatus.channelId && (
                  <div className="flex-1 flex flex-col items-center justify-center gap-2 py-4">
                    <p className={`text-xs text-center leading-relaxed ${muted}`}>
                      Pick a channel in Settings to start seeing messages.
                    </p>
                  </div>
                )}

                {slackStatus?.connected && slackStatus.channelId && (
                  <>
                    {slackMsgLoading && slackMessages.length === 0 && (
                      <p className={`text-xs ${muted}`}>Loading messages...</p>
                    )}
                    {slackMessages.map((msg) => {
                      const ts = parseFloat(msg.ts) * 1000;
                      const timeStr = new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                      const initial = (msg.username || '?')[0].toUpperCase();
                      return (
                        <div key={msg.ts} className="flex items-start gap-2 text-xs">
                          <div className="w-6 h-6 rounded-lg flex-shrink-0 flex items-center justify-center text-white font-bold text-[10px]"
                            style={{ backgroundColor: msg.avatarColor }}>
                            {initial}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-baseline gap-1.5 mb-0.5">
                              <span className={`font-semibold truncate ${theme === 'dark' ? 'text-white/80' : 'text-gray-800'}`}>
                                {msg.username}
                              </span>
                              <span className={`text-[10px] flex-shrink-0 ${muted}`}>{timeStr}</span>
                            </div>
                            <p className={`leading-snug break-words ${muted}`}>{msg.text}</p>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={slackMsgEndRef} />
                  </>
                )}
              </div>

              {slackStatus?.connected && slackStatus.channelId && (
                <div className={`px-3 pb-3 pt-2 flex-shrink-0 border-t ${
                  theme === 'dark' ? 'border-white/5' : 'border-slate-100'
                }`}>
                  <div className="flex gap-2 items-center">
                    <input
                      value={slackReply}
                      onChange={(e) => setSlackReply(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSlackSend()}
                      placeholder={`Message #${slackStatus.channelName}`}
                      className={`flex-1 text-xs px-3 py-2 rounded-2xl outline-none border ${
                        theme === 'dark'
                          ? 'bg-white/5 border-white/10 text-white placeholder-white/25'
                          : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400'
                      }`}
                    />
                    <button
                      onClick={handleSlackSend}
                      disabled={slackSending || !slackReply.trim()}
                      className={`w-8 h-8 rounded-2xl flex items-center justify-center flex-shrink-0 transition disabled:opacity-30 ${
                        theme === 'dark'
                          ? 'bg-white/10 hover:bg-white/20 text-white'
                          : 'bg-slate-100 hover:bg-slate-200 text-slate-600'
                      }`}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M22 2L11 13" /><path d="M22 2L15 22 11 13 2 9l20-7z" />
                      </svg>
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

      </div>{/* end right column */}

      {/* ── Transfer Modal ────────────────────────────────────────────────── */}
      <AnimatePresence>
        {showTransfer && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xl"
            onClick={() => setShowTransfer(false)}>
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 16 }} animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 16 }} transition={{ ease: EASE }}
              className={`w-full max-w-md rounded-[32px] border p-8 shadow-2xl ${panel}`}
              onClick={e => e.stopPropagation()}>
              <div className="text-2xl font-semibold mb-1">Transfer Call</div>
              <div className={`text-sm mb-6 ${muted}`}>Enter destination number or extension.</div>
              <input value={transferTarget} onChange={e => setTransferTarget(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleTransfer()}
                placeholder="Extension or number" autoFocus
                className={`w-full rounded-2xl px-4 py-3 text-lg ${field} ${soft}`}/>
              <div className="mt-6 flex justify-end gap-3">
                <motion.button whileTap={{ scale: 0.96 }} onClick={() => setShowTransfer(false)}
                  className={`rounded-2xl px-5 py-3 text-sm transition ${soft}`}>Cancel</motion.button>
                <motion.button whileTap={{ scale: 0.96 }} onClick={handleTransfer}
                  disabled={!transferTarget.trim()}
                  className="rounded-2xl bg-blue-500 hover:bg-blue-400 px-5 py-3 text-sm font-medium text-white disabled:opacity-40 transition">
                  Transfer Now
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── DTMF Keypad Modal ───────────────────────────────────────────────── */}
      <AnimatePresence>
        {showDtmf && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xl"
            onClick={() => setShowDtmf(false)}>
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 16 }} animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 16 }} transition={{ ease: EASE }}
              className={`w-full max-w-xs rounded-[32px] border p-6 shadow-2xl ${panel}`}
              onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <div className="text-xl font-semibold">Keypad</div>
                <button onClick={() => setShowDtmf(false)}
                  className={`w-7 h-7 rounded-xl flex items-center justify-center transition ${soft} ${muted} hover:text-current`}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
                    <path d="m18 6-12 12"/><path d="m6 6 12 12"/>
                  </svg>
                </button>
              </div>
              <div className={`rounded-2xl px-4 py-3 mb-4 text-center font-mono text-xl tracking-widest min-h-[48px] ${soft}`}>
                {dtmfInput || <span className={muted}>—</span>}
              </div>
              <div className="grid grid-cols-3 gap-2">
                {DIALPAD_KEYS.map(({ key, sub }) => (
                  <motion.button key={key} whileTap={{ scale: 0.92 }}
                    onClick={() => handleDtmf(key)}
                    className={`rounded-2xl py-4 flex flex-col items-center justify-center border border-white/5 transition select-none ${soft}`}>
                    <span className="text-xl font-semibold leading-none">{key}</span>
                    {sub && <span className={`text-[9px] tracking-widest mt-0.5 ${muted}`}>{sub}</span>}
                  </motion.button>
                ))}
              </div>
              <motion.button whileTap={{ scale: 0.96 }} onClick={() => setDtmfInput('')}
                className={`mt-4 w-full rounded-2xl py-3 text-sm transition ${soft} ${muted} hover:text-current`}>
                Clear
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Add / Merge Modal ─────────────────────────────────────────────── */}
      <AnimatePresence>
        {showAddCall && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xl"
            onClick={() => setShowAddCall(false)}>
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 16 }} animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 16 }} transition={{ ease: EASE }}
              className={`w-full max-w-md rounded-[32px] border p-8 shadow-2xl ${panel}`}
              onClick={e => e.stopPropagation()}>
              <div className="text-2xl font-semibold mb-1">Add to Call</div>
              <div className={`text-sm mb-6 ${muted}`}>Dial a number to add to the conversation.</div>
              <input value={addCallTarget} onChange={e => setAddCallTarget(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAddCall()}
                placeholder="Number to add" autoFocus
                className={`w-full rounded-2xl px-4 py-3 text-lg ${field} ${soft}`}/>
              <div className="mt-6 flex justify-end gap-3">
                <motion.button whileTap={{ scale: 0.96 }} onClick={() => setShowAddCall(false)}
                  className={`rounded-2xl px-5 py-3 text-sm transition ${soft}`}>Cancel</motion.button>
                <motion.button whileTap={{ scale: 0.96 }} onClick={handleAddCall}
                  disabled={!addCallTarget.trim()}
                  className="rounded-2xl bg-emerald-500 hover:bg-emerald-400 px-5 py-3 text-sm font-medium text-white disabled:opacity-40 transition">
                  Add to Call
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
